import api from './client';
import { Vehicle, CreateVehicleData, UpdateVehicleData } from '../types/vehicle';

export interface VehicleResponse {
  success: boolean;
  data: Vehicle;
}

export interface VehiclesListResponse {
  success: boolean;
  data: {
    devices: Vehicle[];
    count: number;
  };
}

export interface OBDHealth {
  battery_voltage: number | null;
  dtc_count: number | null;
  dtc_codes: string | null;
  service_distance: number | null;
  coolant_temp: number | null;
  fuel_level: number | null;
  engine_oil_temp: number | null;
  rpm: number | null;
  ignition: number | null;       // 0 = off, 1 = on
  last_speed: number | null;     // km/h
  mil_distance: number | null;
  oem_odometer: number | null;
  updated_at: string | null;
}

export interface VehicleHealthEntry {
  vehicle_id: string;  // backend returns vehicle_id, not _id
  plate_number: string;
  brand: string;
  model: string;
  company_id: string;
  current_odometer?: number;
  obd_health: OBDHealth | null;
}

export interface FleetHealthResponse {
  success: boolean;
  data: {
    vehicles: VehicleHealthEntry[];
    count: number;
  };
}

export const vehiclesApi = {
  /**
   * List all vehicles for the current company
   */
  async listVehicles(): Promise<Vehicle[]> {
    // Add timestamp to bust cache when switching companies
    const timestamp = new Date().getTime();
    const response = await api.get<VehiclesListResponse>(`/api/obd/devices?_t=${timestamp}`);
    return response.data.data.devices;
  },

  /**
   * Create a new vehicle (OBD device assigned separately)
   */
  async createVehicle(data: CreateVehicleData): Promise<Vehicle> {
    const response = await api.post<VehicleResponse>('/api/vehicles', data);
    return response.data.data;
  },

  /**
   * Register a new vehicle with OBD device (legacy / super_admin bundled flow)
   */
  async registerVehicle(data: CreateVehicleData): Promise<Vehicle> {
    const response = await api.post<VehicleResponse>('/api/obd/devices', data);
    return response.data.data;
  },

  /**
   * Update vehicle information
   */
  async updateVehicle(id: string, data: UpdateVehicleData): Promise<Vehicle> {
    const response = await api.patch<VehicleResponse>(`/api/obd/devices/${id}`, data);
    return response.data.data;
  },

  /**
   * Assign driver to vehicle
   */
  async assignDriver(vehicleId: string, driverId: string | null): Promise<Vehicle> {
    const response = await api.patch<VehicleResponse>(`/api/obd/devices/${vehicleId}`, {
      assigned_driver: driverId,
    });
    return response.data.data;
  },

  /**
   * Get vehicle by ID
   */
  async getVehicle(id: string): Promise<Vehicle> {
    const response = await api.get<VehicleResponse>(`/api/obd/devices/${id}`);
    return response.data.data;
  },

  /**
   * Get OBD health data for all vehicles (super_admin only)
   */
  async getFleetHealth(): Promise<VehicleHealthEntry[]> {
    const response = await api.get<FleetHealthResponse>('/api/vehicles/health/all');
    return response.data.data.vehicles;
  },

  /**
   * Get OBD health data for a single vehicle (super_admin only)
   */
  async deleteVehicle(vehicleId: string): Promise<{ success: boolean; data: { deleted_vehicle_id: string; unlinked_obd_device: string | null; unlinked_driver: string | null } }> {
    const response = await api.delete(`/api/vehicles/${vehicleId}`);
    return response.data;
  },

  async getVehicleHealth(id: string): Promise<OBDHealth | null> {
    const response = await api.get<{ success: boolean; data: { obd_health: OBDHealth | null } }>(`/api/vehicles/${id}/health`);
    return response.data.data.obd_health ?? null;
  },

  /**
   * Enable or disable Smartcar integration for a vehicle (company_admin / super_admin only)
   */
  async toggleSmartcar(vehicleId: string, enabled: boolean): Promise<void> {
    await api.patch(`/api/vehicles/${vehicleId}`, { smartcar_enabled: enabled });
  },

  /**
   * Get the vehicle(s) assigned to the currently authenticated driver.
   * Calls GET /api/users/me/vehicle — returns an array of vehicle summaries.
   */
  async getMyVehicle(): Promise<Pick<Vehicle, '_id' | 'plate_number' | 'brand' | 'model' | 'year' | 'status' | 'smartcar_enabled'>[]> {
    const response = await api.get<{ success: boolean; data: Pick<Vehicle, '_id' | 'plate_number' | 'brand' | 'model' | 'year' | 'status' | 'smartcar_enabled'>[] }>('/api/users/me/vehicle');
    return response.data.data;
  },
};
